<template>
  <Transition name="dialog">
    <div
      v-if="show"
      class="fixed inset-0 bg-black/50 z-50 overflow-y-auto"
      @click.self="handleCancel"
    >
      <div class="flex min-h-full items-start justify-center px-6 py-12">
        <div 
          ref="dialogRef"
          class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200"
          @click.stop
        >
          <!-- 標題 -->
          <h3 class="text-lg font-bold text-textMain mb-4">登入帳號</h3>

          <!-- 警告訊息 -->
          <div class="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div class="flex items-start gap-2">
              <span class="text-amber-600 flex-shrink-0">⚠️</span>
              <p class="text-xs text-amber-800 leading-relaxed">
                <span class="font-bold">注意：</span>登入後目前的訪客資料將被清除，帳號資料將切換為您登入的帳號。
              </p>
            </div>
          </div>

          <!-- 說明 -->
          <p class="text-xs text-textMuted mb-4">
            使用您已註冊的信箱和密碼登入。
          </p>

          <!-- 表單 -->
          <div class="space-y-4">
            <!-- Email -->
            <div>
              <label class="block text-sm font-bold text-textMain mb-2">信箱</label>
              <input
                ref="emailInput"
                v-model="email"
                type="email"
                inputmode="email"
                autocomplete="email"
                placeholder="your@email.com"
                class="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:outline-none transition-colors"
                :class="{ 'border-red-400': emailError }"
                @focus="scrollIntoViewOnFocus"
              />
              <p v-if="emailError" class="text-xs text-red-500 mt-1">{{ emailError }}</p>
            </div>

            <!-- Password -->
            <div>
              <label class="block text-sm font-bold text-textMain mb-2">密碼</label>
              <div class="relative">
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  inputmode="text"
                  autocomplete="current-password"
                  placeholder="輸入密碼"
                  class="w-full px-4 py-3 pr-12 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:outline-none transition-colors"
                  :class="{ 'border-red-400': passwordError }"
                  @focus="scrollIntoViewOnFocus"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <!-- 睁开的眼睛 -->
                  <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <!-- 闭上的眼睛 -->
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
              <p v-if="passwordError" class="text-xs text-red-500 mt-1">{{ passwordError }}</p>
            </div>
          </div>

          <!-- 錯誤訊息 -->
          <div v-if="generalError" class="mt-4 p-3 bg-red-50 rounded-xl">
            <p class="text-xs text-red-600">{{ generalError }}</p>
          </div>

          <!-- 按鈕 -->
          <div class="flex gap-3 mt-6">
            <button
              @click="handleCancel"
              class="flex-1 py-3 rounded-xl font-bold text-textMuted bg-gray-100 active:scale-95 transition-all"
              :disabled="loading"
            >
              取消
            </button>
            <button
              @click="handleSubmit"
              class="flex-1 py-3 rounded-xl font-bold text-white bg-primary active:scale-95 transition-all disabled:opacity-50"
              :disabled="loading || !isValid"
            >
              {{ loading ? '登入中...' : '登入' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'

interface Props {
  show: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  confirm: [email: string, password: string]
  cancel: []
}>()

// Template refs
const dialogRef = ref<HTMLElement>()
const emailInput = ref<HTMLInputElement>()

const email = ref('')
const password = ref('')
const loading = ref(false)
const showPassword = ref(false)

const emailError = ref('')
const passwordError = ref('')
const generalError = ref('')

// 當 dialog 打開時，自動 focus 到 email 輸入框
watch(() => props.show, (newShow) => {
  if (newShow) {
    nextTick(() => {
      setTimeout(() => {
        emailInput.value?.focus()
        dialogRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 200)
    })
  }
})

// 當輸入框獲得焦點時，滾動到可見位置
const scrollIntoViewOnFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement
  setTimeout(() => {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }, 300)
}

// Email 格式驗證
const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

// 驗證表單
const isValid = computed(() => {
  return email.value && password.value && !emailError.value && !passwordError.value
})

// 即時驗證 - 當輸入改變時自動驗證
watch(email, () => {
  if (emailError.value) {
    if (!email.value) {
      emailError.value = '請輸入信箱'
    } else if (!validateEmail(email.value)) {
      emailError.value = '信箱格式不正確'
    } else {
      emailError.value = ''
    }
  }
  generalError.value = ''
})

watch(password, () => {
  if (passwordError.value) {
    if (!password.value) {
      passwordError.value = '請輸入密碼'
    } else {
      passwordError.value = ''
    }
  }
  generalError.value = ''
})

// 驗證表單
const validateForm = () => {
  if (!email.value) {
    emailError.value = '請輸入信箱'
  } else if (!validateEmail(email.value)) {
    emailError.value = '信箱格式不正確'
  } else {
    emailError.value = ''
  }

  if (!password.value) {
    passwordError.value = '請輸入密碼'
  } else {
    passwordError.value = ''
  }

  generalError.value = ''
}

const handleSubmit = () => {
  validateForm()

  if (!isValid.value) return

  loading.value = true
  emit('confirm', email.value, password.value)
}

const handleCancel = () => {
  if (loading.value) return
  resetForm()
  emit('cancel')
}

const resetForm = () => {
  email.value = ''
  password.value = ''
  showPassword.value = false
  emailError.value = ''
  passwordError.value = ''
  generalError.value = ''
  loading.value = false
}

const setError = (error: string) => {
  generalError.value = error
  loading.value = false
}

const setLoading = (value: boolean) => {
  loading.value = value
}

defineExpose({
  setError,
  resetForm,
  setLoading
})
</script>

<style scoped>
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoom-in-95 {
  from {
    transform: scale(0.95);
  }
  to {
    transform: scale(1);
  }
}

.animate-in {
  animation: fade-in 0.2s ease, zoom-in-95 0.2s ease;
}
</style>
